import { autocompletion, type CompletionContext, type Completion } from '@codemirror/autocomplete';
import type { JSONSchema7 } from 'json-schema';

const MAX_AUTOCOMPLETE_PATHS = 2000;
const MAX_AUTOCOMPLETE_DEPTH = 8;

/**
 * Extract paths from JSON Schema for autocomplete suggestions.
 * Recursively traverses schema to build dotted paths like "user.email", "items[].price"
 * Includes caps and depth limits to avoid performance issues with large schemas.
 */
function extractSchemaPaths(
  schema: JSONSchema7 | undefined,
  prefix = '',
  acc: string[] = [],
  depth = 0,
): string[] {
  if (!schema) return acc;
  if (depth > MAX_AUTOCOMPLETE_DEPTH) return acc;
  if (acc.length >= MAX_AUTOCOMPLETE_PATHS) return acc;

  // If this node itself is valid, add it
  if (prefix) acc.push(prefix);

  // Handle object properties
  if (schema.type === 'object' && schema.properties) {
    for (const [key, subSchema] of Object.entries(schema.properties)) {
      if (acc.length >= MAX_AUTOCOMPLETE_PATHS) break;
      const newPrefix = prefix ? `${prefix}.${key}` : key;
      if (typeof subSchema === 'object') {
        extractSchemaPaths(subSchema as JSONSchema7, newPrefix, acc, depth + 1);
      } else {
        acc.push(newPrefix);
      }
    }
  }

  // Handle arrays
  if (schema.type === 'array' && schema.items) {
    const itemSchema = Array.isArray(schema.items) ? schema.items[0] : schema.items;
    if (typeof itemSchema === 'object') {
      const arrayPrefix = prefix ? `${prefix}[]` : '[]';
      extractSchemaPaths(itemSchema as JSONSchema7, arrayPrefix, acc, depth + 1);
    }
  }

  return acc;
}

/**
 * Common Liquid filters available for autocomplete
 */
const LIQUID_FILTERS: Completion[] = [
  // String
  { label: 'downcase', type: 'function', detail: 'Lowercase string' },
  { label: 'upcase', type: 'function', detail: 'Uppercase string' },
  { label: 'strip', type: 'function', detail: 'Trim whitespace' },
  { label: 'capitalize', type: 'function', detail: 'Capitalize first letter' },

  // Math
  { label: 'times', type: 'function', detail: 'Multiply by factor' },
  { label: 'plus', type: 'function', detail: 'Add value' },
  { label: 'minus', type: 'function', detail: 'Subtract value' },
  { label: 'divided_by', type: 'function', detail: 'Divide by value' },
  { label: 'modulo', type: 'function', detail: 'Remainder' },
  { label: 'round', type: 'function', detail: 'Round to digits' },
  { label: 'ceil', type: 'function', detail: 'Round up' },
  { label: 'floor', type: 'function', detail: 'Round down' },
  { label: 'abs', type: 'function', detail: 'Absolute value' },

  // Casting
  { label: 'toNumber', type: 'function', detail: 'Convert to number' },
  { label: 'toString', type: 'function', detail: 'Convert to string' },
  { label: 'toBoolean', type: 'function', detail: 'Convert to boolean' },
  { label: 'to_number', type: 'function', detail: 'Convert to number (alias)' },
  { label: 'to_string', type: 'function', detail: 'Convert to string (alias)' },
  { label: 'to_boolean', type: 'function', detail: 'Convert to boolean (alias)' },

  // Fallbacks
  { label: 'default', type: 'function', detail: 'Fallback value when null/empty' },
  { label: 'coalesce', type: 'function', detail: 'First non-empty value' },

  // Aggregates
  { label: 'sum', type: 'function', detail: 'Sum array values' },
  { label: 'sum_by', type: 'function', detail: 'Sum by property' },
  { label: 'avg_by', type: 'function', detail: 'Average by property' },
  { label: 'min_by', type: 'function', detail: 'Minimum by property' },
  { label: 'max_by', type: 'function', detail: 'Maximum by property' },

  // Array helpers
  { label: 'map_by', type: 'function', detail: 'Pluck property from objects' },
  { label: 'filter_by', type: 'function', detail: 'Filter by property value' },
  { label: 'unique', type: 'function', detail: 'Deduplicate array' },
  { label: 'sort_by', type: 'function', detail: 'Sort by property' },

  // Enum
  { label: 'enum_map', type: 'function', detail: 'Map enumerated values' },
  { label: 'enum', type: 'function', detail: 'Map enumerated values (alias)' },

  // Serialization
  { label: 'json', type: 'function', detail: 'JSON stringify' },
];

/**
 * Create Liquid autocomplete extension with schema-aware variable completion
 */
export function liquidAutocomplete(sourceSchema?: JSONSchema7) {
  const schemaPaths = sourceSchema ? extractSchemaPaths(sourceSchema) : [];

  return autocompletion({
    override: [
      (context: CompletionContext) => {
        const word = context.matchBefore(/[\w.[\]]+/);
        if (!word) return null;

        const text = context.state.doc.toString();
        const pos = context.pos;

        // Check if we're inside {{ }}
        const beforeCursor = text.slice(0, pos);
        const lastOpenBrace = beforeCursor.lastIndexOf('{{');
        const lastCloseBrace = beforeCursor.lastIndexOf('}}');

        // Not in a Liquid tag
        if (lastOpenBrace === -1 || lastCloseBrace > lastOpenBrace) {
          return null;
        }

        // Get the content between {{ and cursor
        const tagContent = beforeCursor.slice(lastOpenBrace + 2);

        // Check if we're after a pipe (filter context)
        const lastPipe = tagContent.lastIndexOf('|');
        const isFilterContext = lastPipe !== -1 && tagContent.slice(lastPipe).includes(word.text);

        if (isFilterContext) {
          // Suggest filters
          return {
            from: word.from,
            options: LIQUID_FILTERS,
          };
        }

        // Variable context - suggest input paths
        const options: Completion[] = [];

        // Always suggest "input" as the root
        if ('input'.startsWith(word.text)) {
          options.push({ label: 'input', type: 'variable', detail: 'Source data' });
        }

        // If word starts with "input.", suggest schema paths
        if (word.text.startsWith('input.')) {
          const partialPath = word.text.slice(6); // Remove "input."
          for (const path of schemaPaths) {
            const fullPath = `input.${path}`;
            if (fullPath.startsWith(word.text)) {
              options.push({
                label: fullPath,
                type: 'variable',
                detail: 'Field from source schema',
              });
            }
          }
        }

        return options.length > 0 ? { from: word.from, options } : null;
      },
    ],
  });
}
